/* Journal, local-first storage with optional backend sync.
 *
 * Local key: cm_journal_v1
 * Schema:
 *   { id, ref, refLabel, type ('bible'|'devotional'|'free'),
 *     bookSlug?, chapter?, devotionalId?,
 *     text, created_at, updated_at }
 *
 * Backend (when authenticated): /api/journal CRUD with same schema.
 * Migration helper: collectLocalEntries() returns the full array for
 * one-time upload to the backend on first login.
 */
import axios from "axios";
import { BACKEND_URL } from "./backendBase";

const KEY = "cm_journal_v1";

const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const readLocal = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeLocal = (entries) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {}
};

const newId = () =>
  `j_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const collectLocalEntries = () => readLocal();

export const clearLocalEntries = () => writeLocal([]);

// ---- Local CRUD --------------------------------------------------------------
export const listLocal = () =>
  readLocal().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

export const createLocal = ({ ref, refLabel, type, text, bookSlug, chapter, devotionalId }) => {
  const all = readLocal();
  const now = new Date().toISOString();
  const entry = {
    id: newId(),
    ref: ref || "",
    refLabel: refLabel || ref || "Reflexão livre",
    type: type || "free",
    bookSlug: bookSlug || null,
    chapter: chapter || null,
    devotionalId: devotionalId || null,
    text: String(text || "").trim(),
    created_at: now,
    updated_at: now,
  };
  all.push(entry);
  writeLocal(all);
  return entry;
};

export const updateLocal = (id, { text }) => {
  const all = readLocal();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], text: String(text || "").trim(), updated_at: new Date().toISOString() };
  writeLocal(all);
  return all[idx];
};

export const removeLocal = (id) => {
  const all = readLocal().filter((e) => e.id !== id);
  writeLocal(all);
};

// ---- Backend CRUD ------------------------------------------------------------
export const listRemote = () =>
  api.get("/api/journal").then((r) => r.data);

export const createRemote = (payload) =>
  api.post("/api/journal", payload).then((r) => r.data);

export const updateRemote = (id, payload) =>
  api.patch(`/api/journal/${id}`, payload).then((r) => r.data);

export const removeRemote = (id) =>
  api.delete(`/api/journal/${id}`).then((r) => r.data);

export const migrateRemote = (entries) =>
  api.post("/api/journal/migrate", { entries }).then((r) => r.data);

// ---- Unified API: prefers backend when authenticated, falls back to local ----
export const journalApi = {
  list: async (isAuthed) => {
    if (isAuthed) {
      try { return await listRemote(); } catch { /* fallthrough */ }
    }
    return listLocal();
  },
  create: async (isAuthed, payload) => {
    if (isAuthed) {
      try { return await createRemote(payload); } catch { /* fallthrough */ }
    }
    return createLocal(payload);
  },
  update: async (isAuthed, id, payload) => {
    if (isAuthed) {
      try { return await updateRemote(id, payload); } catch { /* fallthrough */ }
    }
    return updateLocal(id, payload);
  },
  remove: async (isAuthed, id) => {
    if (isAuthed) {
      try { return await removeRemote(id); } catch { /* fallthrough */ }
    }
    removeLocal(id);
    return { ok: true };
  },
};
